import {
  FmlxButton,
  FmlxComboBox,
  FmlxDateRangePicker,
  FmlxDivider,
  FmlxDropdown,
  FmlxTextBox,
} from 'fmlx-common-ui';
import React, { useMemo, useRef, useState } from 'react';
import styles from './AdvancedSearch.module.scss';
import { useSearchBarContext } from '../Context/SearchBarContext';
import { GetUsers } from 'utils/Global';
import { LocationPicker } from './LocationPicker';
import '../SearchBar.scss';
import { SearchItemsTarget } from 'business/models/Models';
import { EDatePreset, SEARCH_BAR_TARGET_LABELS } from '../Context/types';
import { SearchIcon } from '@fmlx/icons';
import { useVisibleChipCount } from './useVisibleChipCount';

const DATE_PRESET_ORDER = [
  EDatePreset.Any,
  EDatePreset.LastWeek,
  EDatePreset.LastMonth,
  EDatePreset.LastThreeMonths,
  EDatePreset.Custom,
];
const DATE_PRESET_OPTIONS = DATE_PRESET_ORDER.map((preset, id) => ({
  id,
  text: preset,
  group: '',
}));

const DAY_MS = 24 * 60 * 60 * 1000;

const getRangeForPreset = (
  preset: EDatePreset,
): { from: Date | null; to: Date | null } => {
  const now = new Date();
  switch (preset) {
    case EDatePreset.LastWeek:
      return { from: new Date(now.getTime() - 7 * DAY_MS), to: now };
    case EDatePreset.LastMonth:
      return { from: new Date(now.getTime() - 30 * DAY_MS), to: now };
    case EDatePreset.LastThreeMonths:
      return { from: new Date(now.getTime() - 90 * DAY_MS), to: now };
    default:
      return { from: null, to: null };
  }
};

const extractTargetId = (item: number | { id: number }): number | null => {
  if (typeof item === 'number') {
    return item;
  }
  if (item && typeof item === 'object' && 'id' in item) {
    return (item as { id: number }).id;
  }
  return null;
};

interface AdvancedSearchProps {
  onSearchClick?: () => void;
}

// Copy of FmlxComboBox's Item interface
interface Item {
  id: number;
  text: string;
  group: string;
}

// HACK: combobox handler fix ported from Barcode preferences combobox
const comboBoxId = 'target-items-combo-box';

const isInputRootBackground = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement && target.classList.contains('MuiOutlinedInput-root');

export const AdvancedSearch = (props: AdvancedSearchProps) => {
  const { onSearchClick } = props;
  const { filters, updateFilters, resetFilters, commitFilters } = useSearchBarContext();
  const [locationSearchText, setLocationSearchText] = useState<string>('');

  const userItems = useMemo(() => {
    const users = GetUsers() ?? [];
    return [...users]
      .filter((u) => u?.id != null)
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
      .map((u) => ({ id: u.id, text: u.name ?? `User ${u.id}`, group: '' }));
  }, []);

  const targetDropdownItems = useMemo(() => {
    return Object.values(SearchItemsTarget)
      .filter(
        (v): v is number => typeof v === 'number' && SEARCH_BAR_TARGET_LABELS[v] != null,
      )
      .map((v) => ({
        id: v,
        text: SEARCH_BAR_TARGET_LABELS[v],
        group: '',
      }));
  }, []);

  const selectedTargetItems = useMemo(() => {
    return targetDropdownItems.filter((item) =>
      filters.targetTypes.includes(item.id as SearchItemsTarget),
    );
  }, [targetDropdownItems, filters.targetTypes]);

  const selectedTargetLabels = useMemo(
    () => selectedTargetItems.map((item) => item.text),
    [selectedTargetItems],
  );

  const handlePresetChange = (presetId: number) => {
    const preset = DATE_PRESET_ORDER[presetId];
    updateFilters({ dateRange: getRangeForPreset(preset), datePreset: preset });
  };

  const handleTargetChange = (e: Item | Item[]) => {
    if (!Array.isArray(e)) {
      return;
    }

    const targetTypes = e
      .map(extractTargetId)
      .filter((id): id is number => id !== null) as SearchItemsTarget[];

    updateFilters({ targetTypes });
  };

  const handleTargetDeleteChip = (deletedItemIdOrPayload: number | { id: number }) => {
    const deletedId = extractTargetId(deletedItemIdOrPayload);
    if (deletedId === null) {
      return;
    }

    updateFilters({
      targetTypes: filters.targetTypes.filter((t) => t !== deletedId),
    });
  };

  const handleReset = () => {
    resetFilters();
  };

  const handleSearchClick = () => {
    commitFilters();
    onSearchClick?.();
  };

  // HACK: combobox handler fix ported from Barcode preferences combobox
  // add a wrapper div around combobox that have ref to combobox
  const wrapperRef = useRef<HTMLDivElement>(null);

  const visibleChipCount = useVisibleChipCount(wrapperRef, selectedTargetLabels);

  // add a handler that check if the combobox is open and prevent it from closing and opening when clicking on the wrapper
  const handleWrapperClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isInputRootBackground(e.target)) {
      return;
    }

    const input = wrapperRef.current?.querySelector(
      `[id="${comboBoxId}"] input[role="combobox"]`,
    ) as HTMLInputElement | null;

    if (input?.getAttribute('aria-expanded') === 'true') {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <div id="advanced-search-wrapper" className={styles.advancedSearch}>
      <div
        id="advanced-search-header"
        className={`${styles.fullWidth} font-medium font-[Roboto] flex flex-row items-center justify-between`}
      >
        Advanced Search
      </div>
      <div className={styles.label}>File Name</div>
      <FmlxTextBox
        id="advanced-search-filename-input"
        placeholder="Enter File Name"
        mode={FmlxTextBox.Mode.TEXT}
        onChange={(e) => updateFilters({ fileName: e.value, query: e.value })}
        value={filters.fileName}
      />
      <div className={styles.label}>Owner</div>
      <FmlxDropdown
        id="advanced-search-owner-dropdown"
        items={userItems}
        selectedItem={filters.ownerId}
        onChange={(id) => updateFilters({ ownerId: id })}
        placeholder={(<div className={styles.placeholderText}>Select Owner</div>) as any}
        menuProps={undefined}
        menuPosition={undefined}
      />
      <div className={styles.label}>Target</div>
      <div
        className="target-items-combo-box-wrapper"
        ref={wrapperRef}
        data-visible-chip-count={visibleChipCount}
        onClickCapture={handleWrapperClickCapture}
      >
        <FmlxComboBox
          id={comboBoxId}
          placeholder={selectedTargetItems.length > 0 ? '' : 'Select Target'}
          items={targetDropdownItems}
          selectedMultipleItem={selectedTargetItems}
          onChange={handleTargetChange}
          onDeleteChip={handleTargetDeleteChip}
          limitTags={visibleChipCount}
          multiple
          disablePortal
        />
      </div>
      <div className={styles.label}>Date Created</div>
      <FmlxDropdown
        id="advanced-search-date-preset-dropdown"
        items={DATE_PRESET_OPTIONS}
        selectedItem={DATE_PRESET_ORDER.indexOf(filters.datePreset)}
        onChange={handlePresetChange}
        menuProps={undefined}
        menuPosition={undefined}
      />
      <div />
      <FmlxDateRangePicker
        labelPosition="top"
        labelStartDate="Start Date"
        labelEndDate="End Date"
        startValue={filters.dateRange?.from ? filters.dateRange.from.toISOString() : ''}
        endValue={filters.dateRange?.to ? filters.dateRange.to.toISOString() : ''}
        onChange={(startValue, endValue) =>
          updateFilters({
            dateRange: {
              from: startValue ? new Date(startValue) : null,
              to: endValue ? new Date(endValue) : null,
            },
          })
        }
        disabled={filters.datePreset != EDatePreset.Custom}
      />
      <div className={styles.label}>Location</div>
      <FmlxTextBox
        id="advanced-search-location-input"
        mode={FmlxTextBox.Mode.TEXT}
        onChange={(e) => setLocationSearchText(e.value)}
        value={locationSearchText}
        placeholder="Search Path"
        decoration={FmlxTextBox.Decoration.ICON}
        icon={<SearchIcon />}
      />
      <div />
      <LocationPicker searchText={locationSearchText} />
      <div className={`${styles.fullWidth} SearchDivider`}>
        <FmlxDivider
          orientation={FmlxDivider.Orientation.HORIZONTAL}
          variant={FmlxDivider.Variant.FULLWIDTH}
          flexItem
        />
      </div>
      <div className={styles.fullWidth}>
        <div className="flex flex-row justify-end gap-x-2">
          <FmlxButton
            id="advanced-search-reset-button"
            label="Reset"
            variant={FmlxButton.Variant.OUTLINE}
            type={FmlxButton.Type.BASIC}
            withTooltip={false}
            onClick={handleReset}
          />
          <FmlxButton
            id="advanced-search-search-button"
            label="Search"
            variant={FmlxButton.Variant.CONTAIN}
            type={FmlxButton.Type.PRIMARY}
            withTooltip={false}
            onClick={handleSearchClick}
          />
        </div>
      </div>
    </div>
  );
};
